/**
 * Rx Scan Session — pure logic for the Medication Scan Assistant.
 *
 * A "scan session" is a transient object that lives entirely in component
 * state. It holds the photos a user has taken (as Blob references — image
 * data is never persisted to the backend except in-flight to the OCR
 * endpoint, then GC'd), per-photo OCR results, per-photo barcode results,
 * and the MERGED candidate fields with conflict detection.
 *
 * Why pure logic in its own module: the merge / conflict-detection rules
 * are the hardest part of the feature and need 100% Jest coverage. The
 * UI component (`MedicationScanAssistant.jsx`) only renders the session
 * state — it never owns the rules.
 *
 * Public API:
 *   - newSession()                              → empty session shape
 *   - addPhoto(session, photo)                  → append a photo
 *   - removePhoto(session, photoId)             → delete (won't break ocr/barcode index)
 *   - replacePhoto(session, photoId, newPhoto)  → retake (same id, new Blob)
 *   - setOcrResult(session, photoId, ocr)       → record OCR response for a photo
 *   - setBarcodeResult(session, photoId, bc)    → record barcode decode for a photo
 *   - mergeCandidates(session)                  → returns { fields, conflicts, status }
 *   - applyUserChoice(session, fieldName, val)  → record a user's conflict pick
 *   - finalConfirmed(session)                   → returns the final prescription payload
 */

const TEXT_FIELDS = [
  "medName", "strength", "directions", "prescriber", "pharmacy",
  "rxNumber", "quantity", "lastFilled", "notes",
];
const NUM_FIELDS = ["daysSupply", "refillsRemaining"];

let _seq = 0;
const _newPhotoId = () => `p-${Date.now()}-${++_seq}`;
const _newSessionId = () => `s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const PHOTO_STEPS = [
  { id: "front",      title: "Front label",                 hint: "Capture the medication name + strength clearly." },
  { id: "directions", title: "Directions / SIG area",       hint: "How to take it — frequency, with food, etc." },
  { id: "pharmacy",   title: "Pharmacy / Rx# / refills",    hint: "Usually the smaller-print block with refills remaining." },
  { id: "barcode",    title: "Barcode",                     hint: "Center the barcode in good lighting. We'll decode automatically." },
  { id: "extra",      title: "Optional extra photo",        hint: "Anything else: warnings, an obscured corner, another side of the bottle.", optional: true },
];

export function newSession() {
  return {
    sessionId: _newSessionId(),
    photos: [],            // [{ id, stepId, blob, url, ocr, barcode }]
    userCorrections: {},   // overrides applied via UI
    conflictChoices: {},   // { fieldName: chosenValue } resolved by the user
    finalConfirmed: null,
  };
}

export function addPhoto(session, { stepId, blob, url }) {
  const photo = { id: _newPhotoId(), stepId, blob, url, ocr: null, barcode: null };
  return { ...session, photos: [...session.photos, photo] };
}

export function removePhoto(session, photoId) {
  return { ...session, photos: session.photos.filter((p) => p.id !== photoId) };
}

export function replacePhoto(session, photoId, { blob, url }) {
  return {
    ...session,
    photos: session.photos.map((p) =>
      p.id === photoId ? { ...p, blob, url, ocr: null, barcode: null } : p,
    ),
  };
}

export function setOcrResult(session, photoId, ocr) {
  return {
    ...session,
    photos: session.photos.map((p) => (p.id === photoId ? { ...p, ocr } : p)),
  };
}

export function setBarcodeResult(session, photoId, barcode) {
  return {
    ...session,
    photos: session.photos.map((p) => (p.id === photoId ? { ...p, barcode } : p)),
  };
}

/**
 * Per-field merge.
 *
 * For each field, gather all (value, confidence, photoStepId) candidates
 * across photos. Then:
 *   - 0 candidates → field empty
 *   - 1 candidate  → use it
 *   - multiple candidates, all canonicalize to the same form → pick the
 *     one with the HIGHEST confidence (length tie-breaker for longer
 *     value when confs are equal — gives "Indomethacin 50 mg" priority
 *     over "Indomethacin" when both are 0.9 confidence)
 *   - multiple canonical forms → CONFLICT (return all distinct values)
 *
 * Canonical = lowercased + whitespace-collapsed + trailing-punctuation-stripped.
 */
function canonical(v) {
  return String(v || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[.,;:!?]+$/g, "")
    .trim();
}

function mergeField(candidates, conflictChoice) {
  const cleaned = candidates.filter((c) => String(c.value || "").trim() !== "");
  if (cleaned.length === 0) return { value: "", confidence: 0, status: "missing" };

  // Bucket by canonical form
  const buckets = new Map();
  for (const c of cleaned) {
    const k = canonical(c.value);
    const bucket = buckets.get(k) || { value: c.value, confidence: c.confidence, count: 0 };
    // Pick the BEST representative — higher confidence wins; ties go to the
    // longer (more complete) value. Tracking both confidence and value
    // independently lets us bubble up a confident, properly-cased string
    // even when both candidates have the same canonical form.
    if (c.confidence > bucket.confidence) {
      bucket.value = c.value;
      bucket.confidence = c.confidence;
    } else if (c.confidence === bucket.confidence && (c.value || "").length > (bucket.value || "").length) {
      bucket.value = c.value;
    }
    bucket.count += 1;
    buckets.set(k, bucket);
  }

  const groups = [...buckets.values()];
  // Single canonical form across all photos — easy case.
  if (groups.length === 1) {
    const g = groups[0];
    return {
      value: g.value,
      confidence: g.confidence,
      status: g.confidence >= 0.8 ? "high" : g.confidence >= 0.5 ? "review" : "low",
      candidates: groups,
    };
  }

  // Multiple distinct values → conflict.
  // User may have already resolved this with applyUserChoice.
  if (conflictChoice != null) {
    const chosen = groups.find((g) => canonical(g.value) === canonical(conflictChoice))
      || { value: conflictChoice, confidence: 0.9 };
    return {
      value: chosen.value,
      confidence: chosen.confidence,
      status: "resolved",
      candidates: groups,
    };
  }
  // Sort groups by (confidence desc, count desc) so the top option is the
  // recommended pick in the UI.
  groups.sort((a, b) => (b.confidence - a.confidence) || (b.count - a.count));
  return {
    value: "",
    confidence: groups[0].confidence,
    status: "conflict",
    candidates: groups,
  };
}

/**
 * Pick the best barcode across photos: highest confidence (the decoder
 * doesn't return confidence, so we just take the FIRST decoded barcode —
 * but de-dupe on rawValue so two photos of the same bar don't multiply).
 */
function mergeBarcode(photos) {
  const bcs = photos.map((p) => p.barcode).filter(Boolean);
  if (bcs.length === 0) return { rawValue: "", format: "", source: "" };
  // Prefer barcodes from the photo whose step was "barcode" (intentional capture).
  const intentional = photos.find((p) => p.stepId === "barcode" && p.barcode);
  const winner = intentional?.barcode || bcs[0];
  return {
    rawValue: winner.text || winner.rawValue || "",
    format: winner.format || "",
    source: winner.source || "",
  };
}

export function mergeCandidates(session) {
  // Build per-field candidate list from each photo's OCR result.
  const fields = {};
  const conflicts = [];

  for (const f of TEXT_FIELDS) {
    const candidates = session.photos
      .map((p) => p.ocr ? ({
        value: p.ocr[f],
        confidence: p.ocr.confidence?.[f] ?? 0,
        photoStepId: p.stepId,
      }) : null)
      .filter(Boolean);
    const merged = mergeField(candidates, session.conflictChoices[f]);
    fields[f] = merged;
    if (merged.status === "conflict") conflicts.push(f);
  }

  for (const f of NUM_FIELDS) {
    const candidates = session.photos
      .map((p) => p.ocr ? ({
        value: typeof p.ocr[f] === "number" && p.ocr[f] > 0 ? String(p.ocr[f]) : "",
        confidence: p.ocr.confidence?.[f] ?? 0,
        photoStepId: p.stepId,
      }) : null)
      .filter(Boolean);
    const merged = mergeField(candidates, session.conflictChoices[f]);
    fields[f] = {
      ...merged,
      value: merged.value ? Number(merged.value) || 0 : 0,
    };
    if (merged.status === "conflict") conflicts.push(f);
  }

  const barcode = mergeBarcode(session.photos);

  // Status banner: "high" if everything is high; "needs-review" if any low/review;
  // "conflict" if any unresolved conflicts; "missing" if nothing extracted at all.
  let status = "high";
  const statuses = Object.values(fields).map((f) => f.status);
  if (statuses.every((s) => s === "missing")) status = "missing";
  else if (statuses.includes("conflict")) status = "conflict";
  else if (statuses.some((s) => s === "low" || s === "review")) status = "needs-review";

  return { fields, conflicts, barcode, status };
}

export function applyUserChoice(session, fieldName, value) {
  return {
    ...session,
    conflictChoices: { ...session.conflictChoices, [fieldName]: value },
  };
}

/**
 * Build the prescription payload to hand back to the form / save endpoint.
 * Applies user corrections on top of merged candidates. Marks every
 * scanner-filled field in `scannedFields` so the UI can badge them
 * "needs confirmation" until the user verifies them.
 */
export function finalConfirmed(session, userOverrides = {}) {
  const merge = mergeCandidates(session);
  const out = {};
  const scannedFields = [];
  const confidence = {};
  for (const f of [...TEXT_FIELDS, ...NUM_FIELDS]) {
    if (userOverrides[f] !== undefined && userOverrides[f] !== "") {
      out[f] = userOverrides[f];
      // user-set → not "scanned/needs confirmation"
    } else {
      out[f] = merge.fields[f].value;
      if (out[f] !== "" && out[f] !== 0) scannedFields.push(f);
    }
    confidence[f] = merge.fields[f].confidence || 0;
  }
  if (merge.barcode.rawValue) {
    out.barcodeData = merge.barcode.rawValue;
    out.barcodeFormat = merge.barcode.format || "";
  }
  out.scanConfidence = confidence;
  out.scannedFields = scannedFields;
  return out;
}
